import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import NoData from '@components/NoData';
import { toastError, toastSuccess } from '@components/Toast';

import EditButton from '@features/supplier/component/EditButton';
import ViewAllPopup from '@features/supplier/component/ViewAllPopup';

import { fetchBrandList } from '@store/actions/BrandAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchSupplierDetails } from '@store/supplier/action';

import { removeKeyCustomerBrand } from '@services/supplierOnboardingService';
import { fetchSupplierData } from '@services/supplierService';

import ApiClient from '@lib/apiClient';

import { capitalizeFirstLetter, printApiError } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const KeyCustomers = ({ detailsInfo }) => {
	const [supplierInfo, setSupplierInfo] = useState(null);
	const [isEdit, setIsEdit] = useState(false);
	const [nonNominatedBrands, setNonNominatedBrands] = useState([]);
	const [selectedBrandId, setSelectedBrandId] = useState(null);
	const [productResponse, setProductResponse] = useState({});
	const [tagList, setTagList] = useState([]);
	const [keyCustomerList, setKeyCustomerList] = useState([]);
	const dispatch = useDispatch();
	const { id } = useParams();
	const myStateRef = useRef({});

	let brandList = useSelector((store) => store.brandStore);
	brandList = brandList?.map((item) => ({ key: item.id, value: item.name }));
	const cloneBrands = useSelector((store) => store.brandStore);

	useEffect(() => {
		setSupplierInfo(detailsInfo);
	}, [detailsInfo]);

	useEffect(() => {
		dispatch(fetchBrandList());
	}, []);

	useEffect(() => {
		let supplierBrandList = supplierInfo?.brandResponseList;
		if (supplierBrandList) {
			let tmpArray = brandList.filter(
				(obj) => !supplierBrandList.find((item) => item.brandId === obj.key)
			);
			setNonNominatedBrands(tmpArray);
			let modifiedList = supplierBrandList?.map((item) => ({
				brand: { value: item.BrandName, key: item.brandId },
				productionVolume: item.productionVolume,
				id: item.id
			}));
			setKeyCustomerList(modifiedList);
		} else {
			setKeyCustomerList([{ brand: { key: null, value: null }, productionVolume: null, id: null }]);
			setNonNominatedBrands(brandList);
		}
	}, [cloneBrands, supplierInfo?.brandResponseList]);

	const onFetchProductTag = async () => {
		const response = await fetchSupplierData({
			supplierId: id,
			tagType: 'GARMENTS',
			type: 'PRODUCT_TAG'
		});
		setTagList(response);
	};

	const onItemClick = async (item, isNominated, isDeletable) => {
		dispatch(openLoader());
		let params = `?isNominated=${isNominated}&isDeletable=${isDeletable}`;
		if (item?.rowId) {
			params += `&id=${item.rowId}`;
		}
		if (item?.key) {
			params += `&brandId=${item.key}`;
		}
		if (item?.productionVolume) {
			params += `&productionVolume=${item.productionVolume}`;
		}
		await ApiClient.POST(`${BASE_URL}/supplier/${id}/brands`, '', params, true)
			.then(() => {
				dispatch(fetchSupplierDetails(id));
				toastSuccess('Brand nominated');
				dispatch(closeLoader());
			})
			.catch(({ response }) => {
				dispatch(closeLoader());
				toastError(response.data.message);
			});
	};

	const nonNominatedSupplier = async (obj, index, selectedId) => {
		let modifiedList = keyCustomerList?.map((item, i) => {
			if (i === index) {
				item.brand = obj;
			}
			return item;
		});
		let postData = obj;
		if (selectedId) {
			postData.rowId = selectedId;
		}
		await onItemClick(postData, false, true);
		setKeyCustomerList(modifiedList);
	};

	const saveProductionVolume = async (value, index, selectedId) => {
		let postData = { productionVolume: value };
		if (selectedId) {
			postData.rowId = selectedId;
		}
		await onItemClick(postData, false, true);
	};

	const onChangeProductionVolume = async (value, index) => {
		let modifiedList = keyCustomerList?.map((item, i) => {
			if (i === index) {
				item.productionVolume = value;
			}
			return item;
		});
		setKeyCustomerList(modifiedList);
	};

	const setMyState = (data) => {
		myStateRef.current = data;
	};

	useEffect(() => {
		if (selectedBrandId) {
			onFetchProductTag();
			fetchGarments();
		}
	}, [selectedBrandId]);

	const fetchGarments = (page = 0) => {
		dispatch(openLoader());
		let params = `?supplierId=${id}&page=${page}&size=12&brandIds=${selectedBrandId}`;
		ApiClient.GET('fetchGarments', params)
			.then(({ data }) => {
				if (data.currentPage > 0) {
					data.data = [...myStateRef.current.data, ...data.data];
				}
				setMyState(data);
				setProductResponse(data);
				dispatch(closeLoader());
			})
			.catch((error) => {
				dispatch(closeLoader());
				toastError(error.response.data.message);
			});
	};

	const onSelectBrandId = (id) => {
		setSelectedBrandId(id);
	};

	const productsToRender = productResponse?.data;

	const renderTagList = (list, title) => {
		return (
			<div className="categories">
				{list?.length > 0 &&
					list?.map((item) => (
						<Tooltip
							title={<span className="regular-14 ">{title}</span>}
							placement="top"
							arrow
							key={item?.id}
						>
							<span className="status cyan-1 radius regular-12 mr-2 cursor-pointer">
								{title === 'Weight range' ? item?.name : capitalizeFirstLetter(item?.name)}
							</span>
						</Tooltip>
					))}
			</div>
		);
	};

	const addNewKeyCustomer = () => {
		setKeyCustomerList((prev) => [
			...prev,
			{ brand: { label: null, value: null }, productionVolume: null, id: null }
		]);
	};

	const removeKeyCustomer = async (index, selectedId) => {
		if (selectedId) {
			try {
				dispatch(openLoader());
				await removeKeyCustomerBrand(id, selectedId);
				await dispatch(fetchSupplierDetails(id));
				dispatch(closeLoader());
			} catch (e) {
				dispatch(closeLoader());
				printApiError(e);
			}
		} else {
			let remainingCustomers = keyCustomerList.filter((item, i) => i !== index);
			setKeyCustomerList(remainingCustomers);
		}
	};

	return (
		<>
			<div className="customers-section section-padding hover-section">
				<h3 className="suplier-title pb-4 font-weight-bold regular-16">
					Key customers
					<EditButton onEditClick={() => setIsEdit(true)} />
				</h3>
				{supplierInfo?.brandResponseList?.length > 0 ? (
					<div className="customers-items">
						{supplierInfo?.brandResponseList?.map((item) => (
							<div
								className="single same-padding-box cursor-pointer"
								key={item.id}
								onClick={() => onSelectBrandId(item.brandId)}
							>
								<div className="customer-img">
									{item?.brandLogoDocResponse?.docUrl ? (
										<img src={item?.brandLogoDocResponse?.docUrl} alt={item?.BrandName} />
									) : (
										<span className="demo-name gray_dark_03 weight-500">
											{item?.BrandName}
										</span>
									)}
									{item?.productionVolume && (
										<p className="regular-12 gray_dark_03 weight-500 mt-12">
											Volume: {item.productionVolume}
										</p>
									)}
								</div>

								{item?.isNominated && (
									<span className="regular-12 status blue-1 radius">Nominated</span>
								)}
							</div>
						))}
					</div>
				) : (
					<NoData message="No key customer added" />
				)}
			</div>
			{isEdit && (
				<ModalContainer className="key-customer-modal">
					<div className="common-popup ">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Add Key Customers</h3>
							</div>
							<div className="close-btn" onClick={() => setIsEdit(false)}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>

						<div className="common-popup-body scroll-y-label">
							<div className="key-customer-content">
								{keyCustomerList?.map((item, i) => (
									<div key={`key_customer_${i}`} className="row remove-row">
										<div className="col-md-6">
											<div className="form-group">
												<CustomDropdown
													type="addItem"
													title={'Select Brand'}
													onItemClick={(selected) =>
														nonNominatedSupplier(
															selected,
															i,
															keyCustomerList[i]?.id
														)
													}
													isDisabled={
														keyCustomerList[i]?.id &&
														keyCustomerList[i]?.brand?.value
													}
													items={nonNominatedBrands}
													selectedItem={keyCustomerList[i]?.brand?.value}
												/>
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group">
												<input
													type="text"
													className="w-100"
													placeholder="Volume(%)"
													value={keyCustomerList[i].productionVolume}
													onChange={(e) =>
														onChangeProductionVolume(e.target.value, i)
													}
													onBlur={(e) =>
														saveProductionVolume(
															e.target.value,
															i,
															keyCustomerList[i]?.id
														)
													}
												/>
											</div>
										</div>
										{keyCustomerList?.length > 1 && (
											<div
												className="remove-img"
												onClick={() =>
													removeKeyCustomer(i, keyCustomerList[i]?.brand.key)
												}
											>
												<button className="border-0 bg-transparent">
													<img
														src={`${process.env.PUBLIC_URL}/icons/minus-round.svg`}
														alt="close"
													/>
												</button>
											</div>
										)}
									</div>
								))}
								<div className="col-md-12 p-0" onClick={addNewKeyCustomer}>
									<button className="button text size32 p-0">
										<span className="weight-500">Add</span>
										<img
											src={`${process.env.PUBLIC_URL}/icons/add-round.svg`}
											alt="add"
										/>
									</button>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32"
									onClick={() => setIsEdit(false)}
								>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={() => setIsEdit(false)}>
									Submit
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{selectedBrandId && (
				<ViewAllPopup
					supplierType={'GARMENTS'}
					onClose={() => setSelectedBrandId(null)}
					productList={productsToRender}
					callback={(page) => {
						fetchGarments(page);
					}}
					productResponse={productResponse}
					renderTypeWiseTag={() =>
						renderTagList(tagList?.productCategoryResponseList, 'Product Category')
					}
				/>
			)}
		</>
	);
};

export default KeyCustomers;
